/**
 * Helpers for the store-based conditional outcome (store-group-matching-rule).
 *
 * The persisted `ruleData` shape is:
 *   {
 *     keywords: string[],              // matched at runtime against additional_prop.vendor_name
 *     store_configuration: {           // metadata snapshot -> publicRuleData (FE/WSP rendering)
 *       selection_type: "store" | "store_group",
 *       category: string,
 *       stores: Array<{ id, name, retailer, address, postcode, city, country, geolocation }>,
 *     }
 *   }
 *
 * Both fields are real persisted data, so no save/load transform is needed — the
 * generic `{ ruleDefinitionSlug, ruleData }` mapping handles it, and rehydration
 * reads `store_configuration` back directly.
 */

export const STORE_GROUP_RULE_SLUG = "store-group-matching-rule";

// Slug of the OUTLET metadata `ui_component` seeded per program in admin-database-settings
// (data/<market>/ui_components/outlet-metadata-schema.json). Its `ui_schema.fields` drive
// the store table columns and the persisted `store_configuration` snapshot — see
// useOutletMetadataSchema. Loaded through the generic loadUiComponent(slug) query.
export const OUTLET_METADATA_SCHEMA_SLUG = "outlet-metadata-schema";

export const STORE_SELECTION_TYPES = {
  STORE: "store",
  STORE_GROUP: "store_group",
};

/**
 * Maps an OUTLET keyword group (Store) to the metadata snapshot persisted in
 * `store_configuration.stores`, using the OUTLET metadata schema `fields` as the
 * source of truth for which paths to capture (no hardcoded field list). The `name`
 * field falls back to the group name when the metadata name is missing; every other
 * field defaults to `null` when absent.
 *
 * @param {{ id: string, name?: string, metadata?: object }} store
 * @param {Array<{ path: string }>} fields  OUTLET metadata schema fields
 */
export const buildStoreConfigEntry = (store, fields = []) => {
  const metadata = store?.metadata || {};
  const entry = { id: store?.id };
  fields.forEach(({ path }) => {
    if (path === "name") {
      entry.name = metadata.name || store?.name || "";
    } else {
      entry[path] = metadata[path] ?? null;
    }
  });
  return entry;
};

/**
 * Builds the persisted `ruleData` from a store/store-group selection.
 *
 * - Specific Stores   -> keywords = de-duplicated union across selected Stores;
 *                        stores = the selected Stores.
 * - Store Group       -> keywords = de-duplicated union across the category;
 *                        stores = every store in the category.
 *
 * @param {{ selectionType: string, category: string, stores: Array, fields?: Array }} selection
 * @returns {{ keywords: string[], store_configuration: object }}
 */
export const buildStoreRuleData = ({
  selectionType,
  category,
  stores = [],
  fields = [],
}) => {
  const keywords = [
    ...new Set(stores.flatMap((store) => store?.keywords || []).filter(Boolean)),
  ];

  return {
    keywords,
    store_configuration: {
      selection_type: selectionType,
      category: category ?? null,
      stores: stores.map((store) => buildStoreConfigEntry(store, fields)),
    },
  };
};

/**
 * Reconstructs the UI selection from persisted `ruleData` for edit mode.
 *
 * @param {object} ruleData
 * @returns {{ selectionType: string, category: string, storeIds: string[] } | null}
 */
export const getStoreSelectionFromRuleData = (ruleData) => {
  const config = ruleData?.store_configuration;
  if (!config) return null;
  return {
    selectionType: config.selection_type || STORE_SELECTION_TYPES.STORE,
    category: config.category || null,
    storeIds: (config.stores || []).map((store) => store.id).filter(Boolean),
  };
};

/**
 * A store condition is configured once a Store/Store Group has been selected,
 * which produces at least one keyword and a store_configuration snapshot.
 */
export const isStoreConditionConfigured = (ruleData) => {
  return (
    !!ruleData?.store_configuration &&
    Array.isArray(ruleData?.keywords) &&
    ruleData.keywords.length > 0
  );
};
